import { useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Menu as MenuIcon,
  PersonRounded as PersonIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fade,
  IconButton,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { makeStyles, styled } from "@mui/styles";

import { ReactComponent as ArrowDownSvg } from "assets/images/icons/expand_more.svg";
import { IMenuItem, MenuItems } from "common";
import SharedLink from "components/shared/Link";
import useAuth from "hooks/useAuth";
import { colors } from "theme";

const useStyles = makeStyles((theme: Theme) => ({
  menu: {
    "& .MuiMenu-paper": {
      [theme.breakpoints.down(1024)]: {
        maxWidth: "unset",
        right: 0,
      },
    },
  },
}));

const AssayMarketplaceLink = () => {
  const history = useHistory();
  const location = useLocation();
  const handleKeyDown = (ev: any) => {
    // listens for enter key when link is focused
    if (ev.keyCode === 13) {
      history.push("/");
    }
  };
  return (
    <AMPlink
      onClick={() => history.push("/")}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className={`${
        location.pathname === "/custom-panels"
          ? ""
          : location.pathname === "/technology-platform"
          ? ""
          : "active"
      }`}
    >
      Assay Marketplace
    </AMPlink>
  );
};

const CustomPanelsLink = () => {
  const history = useHistory();
  const location = useLocation();
  const handleKeyDown = (e: any) => {
    // listens for enter key when link is focused
    if (e.keyCode === 13) {
      history.push("/custom-panels");
    }
  };
  return (
    <Link
      onClick={(event: React.MouseEvent<HTMLElement>) => {
        const redirectUrl = "/custom-panels";
        event.ctrlKey
          ? window.open(redirectUrl, "_blank")
          : history.push(redirectUrl);
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className={`${location.pathname === "/custom-panels" ? "active" : ""}`}
    >
      Custom Panels
    </Link>
  );
};

const TechnologyPlatformLink = () => {
  const history = useHistory();
  const location = useLocation();
  const handleKeyDown = (e: any) => {
    // listens for enter key when link is focused
    if (e.keyCode === 13) {
      history.push("/technology-platform");
    }
  };
  return (
    <Link
      url="/technology-platform"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className={`${
        location.pathname === "/technology-platform" ? "active" : ""
      }`}
    >
      Technology Platform
    </Link>
  );
};

const ActionButton = () => {
  const history = useHistory();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(1024)
  );
  const classes = useStyles();

  const { hasPermission, logout, user } = useAuth();

  const profileRef = useRef<HTMLButtonElement>(null);
  const [expanded, setExpanded] = useState(false);

  if (user) {
    return (
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ProfileButton
          ref={profileRef}
          onClick={() => setExpanded(true)}
          aria-label="Open user navigation menu"
          aria-haspopup="true"
          aria-controls="userMenuNav"
        >
          <Box
            color={isMobile && !expanded ? colors.nero[300] : "primary.main"}
          >
            <PersonIcon />
          </Box>
        </ProfileButton>

        {isMobile && (
          <ExpandButton
            size="small"
            className={expanded ? "rotate" : "rotated"}
            onClick={() => setExpanded((expanded) => !expanded)}
          >
            <ArrowDownSvg />
          </ExpandButton>
        )}

        <Menu
          id="userMenuNav"
          anchorEl={profileRef.current}
          anchorReference={isMobile ? "anchorPosition" : "anchorEl"}
          anchorPosition={{ left: 0, top: 254 }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={Boolean(profileRef.current) && expanded}
          onClose={() => setExpanded(false)}
          onClick={() => setExpanded(false)}
          elevation={isMobile ? 0 : 1}
          PaperProps={{ square: isMobile }}
          className={classes.menu}
        >
          {MenuItems.filter(
            (menuItem: IMenuItem) =>
              !menuItem.validRoles || hasPermission(menuItem.validRoles)
          ).map((menuItem: IMenuItem, index: number) => (
            <MenuItem
              key={index}
              divider
              onClick={() => history.push(menuItem.link)}
            >
              {menuItem.label}
            </MenuItem>
          ))}
          <MenuItem onClick={logout}>Sign Out</MenuItem>
        </Menu>
      </Box>
    );
  }

  return (
    <Button fullWidth={isMobile} onClick={() => history.push("/login")}>
      Sign In
    </Button>
  );
};

export const InvitaeNavRight = () => {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(1024)
  );

  const [collapsed, setCollapsed] = useState(true);

  return (
    <Box display="flex" alignItems="center" columnGap={isMobile ? "unset" : 3}>
      <AssayMarketplaceLink />
      {isMobile && (
        <>
          <MobileMenuButton
            size="small"
            onClick={() => {
              setCollapsed((collapsed) => !collapsed);
            }}
          >
            <Box
              height={24}
              color={collapsed ? colors.nero[300] : "primary.main"}
            >
              <MenuIcon />
            </Box>
          </MobileMenuButton>
          <Fade in={!collapsed}>
            <Box
              position="absolute"
              left={0}
              right={0}
              top={80}
              bgcolor="white"
              px={2}
              py="10px"
              display="flex"
              flexDirection="column"
              zIndex={1201}
              borderTop={`1px solid ${colors.grey[500]}`}
              rowGap={2}
            >
              <CustomPanelsLink />
              <TechnologyPlatformLink />
              <ActionButton />
            </Box>
          </Fade>
        </>
      )}
      {!isMobile && (
        <>
          <CustomPanelsLink />
          <TechnologyPlatformLink />
          <ActionButton />
        </>
      )}
    </Box>
  );
};

const ProfileButton = styled(IconButton)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "unset",
    "& svg": {
      color: theme.palette.primary.dark,
    },
  },
  "& .MuiIconButton-label": {
    width: 18,
    height: 18,
  },
  [theme.breakpoints.up(1024)]: { marginRight: theme.spacing(-1) },
  [theme.breakpoints.between(960, 1024)]: { marginLeft: theme.spacing(2) },
  [theme.breakpoints.between(420, 960)]: { marginLeft: theme.spacing(1) },
  [theme.breakpoints.down(420)]: { marginLeft: theme.spacing(-1) },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "rgba(35, 8, 113, 0.1)",
  },
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  "&.rotate": {
    "& svg": { transform: "rotate(0deg)" },
  },
  "&.rotated": {
    "& svg": { transform: "rotate(-90deg)" },
  },
  "& path": {
    fill: theme.palette.primary.main,
  },
  [theme.breakpoints.between(960, 1024)]: {
    "&.rotate": { marginRight: theme.spacing(4) },
    "&.rotated": { marginRight: theme.spacing(2) },
  },
}));

const Menu = styled(MuiMenu)(({ theme }) => ({
  "& .MuiMenu-paper": {
    minWidth: 192,
    [theme.breakpoints.up(1024)]: {
      boxShadow: "0px 0px 20px rgba(40, 41, 44, 0.12)",
    },
    [theme.breakpoints.down(1024)]: {
      borderTop: `solid 4px ${theme.palette.grey[500]}`,
      boxShadow: "0px 8px 20px 0px rgba(40, 41, 44, 0.12)",
    },
  },
  "& .MuiPaper-elevation0": {
    left: "0 !important",
  },
  "& .MuiMenu-list": {
    padding: 0,
  },
}));

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  fontSize: 18,
  lineHeight: "26.24px",
  padding: "15px 16px",
  borderBottomColor: theme.palette.grey[500],
  "&:hover": {
    backgroundColor: theme.palette.grey[500],
  },
}));

const AMPlink = styled(SharedLink)(({ theme }) => ({
  color: colors.nero[300],
  fontWeight: 600,
  [theme.breakpoints.up(1024)]: {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(2),
    margin: "3px 10px 0px",
    "&:hover": {
      borderBottom: "4px solid",
      borderBottomColor: theme.palette.primary.main,
      marginBottom: -4,
    },
  },
  [theme.breakpoints.down(1024)]: { marginRight: theme.spacing(1) },
  "&.active": { color: theme.palette.text.primary },
  "&.MuiLink-underlineNone.MuiTypography-colorPrimary:hover": {
    color: theme.palette.primary.main,
  },
}));

const Link = styled(SharedLink)(({ theme }) => ({
  color: colors.nero[300],
  fontWeight: 600,
  [theme.breakpoints.up(1024)]: {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(2),
    margin: "3px 10px 0px",
    "&:hover": {
      borderBottom: "4px solid",
      borderBottomColor: theme.palette.primary.main,
      marginBottom: -4,
    },
    "& + button": { marginLeft: 12 },
  },
  [theme.breakpoints.down(1024)]: {
    paddingTop: 8,
    paddingBottom: 8,
    "&:hover": { backgroundColor: theme.palette.grey[600] },
  },
  [theme.breakpoints.between(960, 1024)]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  [theme.breakpoints.between(420, 960)]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  "&.active": { color: theme.palette.text.primary },
  "&.MuiLink-underlineNone.MuiTypography-colorPrimary:hover": {
    color: theme.palette.primary.main,
  },
}));
